import { Progress } from "@/common/components/ui/progress";
import { Badge } from "@/common/components/ui/badge";
import { CalendarDays } from "lucide-react";
import { SectionTitle } from "../utils/helpers.jsx";

export default function AssessmentList({
  assessments,
  title,
  isTableView = false,
}) {
  
  // Table view format
  if (isTableView) {
    // Get status band based on percentage
    const getProgressBand = (percentage) => {
      if (percentage >= 75) return "Excellent";
      if (percentage >= 60) return "Good";
      if (percentage >= 40) return "Average";
      return "Needs Improvement";
    };

    // Get band class for styling
    const getBandClass = (percentage) => {
      if (percentage >= 75) return "bg-green-100 text-green-800";
      if (percentage >= 60) return "bg-blue-100 text-blue-800";
      if (percentage >= 40) return "bg-yellow-100 text-yellow-800";
      return "bg-red-100 text-red-800";
    };

    return (
      <div className="mb-8">
        <SectionTitle
          title={
            title.includes("Skill")
              ? "🧠 Skill Assessment Report"
              : "📝 Assessment Report"
          }
        />
        {assessments.length > 0 ? (
          <div className="overflow-x-auto print:overflow-visible">
            <table className="w-full border-collapse border border-gray-200 print:border-collapse print:text-xs">
              <thead>
                <tr className="bg-gray-50 print:bg-gray-100">
                  <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                    Assessment Name
                  </th>
                  <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                    Completion Date
                  </th>
                  <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                    Status
                  </th>
                  <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                    Max Marks
                  </th>
                  <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                    Marks Obtained
                  </th>
                  <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                    Score (%)
                  </th>
                  <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                    Performance
                  </th>
                </tr>
              </thead>
              <tbody>
                {assessments.map((assessment) => (
                  <tr key={assessment.id}>
                    <td className="px-4 py-3 border font-medium print:py-2 max-w-[200px]">
                      <div className="w-full break-words overflow-wrap-anywhere overflow-hidden">
                        {assessment.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 border print:py-2 whitespace-normal">
                      {assessment.submittedOn}
                    </td>
                    <td className="px-4 py-3 border print:py-2">
                      {assessment.status === "completed" ? (
                        <span className="px-2 py-1 text-xs text-green-700 bg-green-100 border-green-200 rounded print:font-bold print:py-0">
                          Completed
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs text-yellow-700 bg-yellow-100 border-yellow-200 rounded print:font-bold print:py-0">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 border text-center font-medium print:py-2">
                      {assessment.max_marks ?? 'N/A'}
                    </td>
                    <td className="px-4 py-3 border text-center font-medium print:py-2">
                      {assessment.marks_obtained ?? 'N/A'}
                    </td>
                    <td className="px-4 py-3 border text-center font-medium print:py-2">
                      {assessment.percentage}%
                    </td>
                    <td className="px-4 py-3 border print:py-2">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${getBandClass(
                          assessment.percentage
                        )} print:font-bold print:py-0`}
                      >
                        {getProgressBand(assessment.percentage)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500 border border-gray-200 rounded-lg">
            No {title.toLowerCase()} available
          </div>
        )}
      </div>
    );
  }

  // Original card view
  return (
    <div className="mb-8">
      <SectionTitle title={title} />
      {assessments.length > 0 ? (
        <div className="space-y-4">
          {assessments.map((assessment) => (
            <div
              key={assessment.id}
              className="p-3 border border-gray-200 rounded-lg shadow-sm md:p-4"
            >
              <div className="space-y-2 md:space-y-3">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold md:text-lg">
                      {assessment.name}
                    </h3>
                    {assessment.status === "completed" ? (
                      <Badge className="text-xs text-green-700 bg-green-100 border-green-200 whitespace-nowrap">
                        Completed
                      </Badge>
                    ) : (
                      <Badge className="text-xs text-yellow-700 bg-yellow-100 border-yellow-200 whitespace-nowrap">
                        Pending
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-4 text-xs md:text-sm text-slate-500">
                    <div className="flex items-center gap-1">
                      <CalendarDays className="w-4 h-4" />
                      <span className="whitespace-nowrap">
                        {assessment.submittedOn}
                      </span>
                    </div>
                  </div>
                  {(assessment.max_marks !== undefined || assessment.marks_obtained !== undefined) && (
                    <div className="flex gap-2 text-xs md:text-sm text-slate-600">
                      <span className="font-medium">Marks:</span>
                      <span>
                        {assessment.marks_obtained ?? 'N/A'} / {assessment.max_marks ?? 'N/A'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between text-xs md:text-sm">
                    <span>Percentage</span>
                    <span className="font-medium">
                      {assessment.percentage}%
                    </span>
                  </div>
                  <Progress
                    value={assessment.percentage}
                    className="h-2"
                    style={{
                      backgroundColor: "#e2e8f0",
                      "--progress-color":
                        assessment.percentage < 30
                          ? "#ef4444"
                          : assessment.percentage < 70
                          ? "#f59e0b"
                          : "#22c55e",
                      "--progress-value": `${assessment.percentage}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500">
          No {title.toLowerCase()} available
        </div>
      )}
    </div>
  );
}
